import React, { useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/providers/auth-provider";
import { canAccess, canAccessRoute, type FeatureKey } from "@/lib/permissions";
import { AlertTriangle } from "lucide-react";

interface PermissionGuardProps {
  feature: FeatureKey;
  children: React.ReactNode;
  /** If true, redirects to dashboard instead of showing access denied */
  redirect?: boolean;
  /** Custom fallback component */
  fallback?: React.ReactNode;
}

/**
 * Wraps content that requires a specific permission.
 * Hides content AND blocks access for unauthorized users.
 */
export function PermissionGuard({
  feature,
  children,
  redirect = false,
  fallback,
}: PermissionGuardProps) {
  const { user } = useAuth();
  const [, navigate] = useLocation();

  const hasAccess = canAccess(feature, user);

  useEffect(() => {
    if (!hasAccess && redirect) {
      // Redirect to the correct dashboard based on role
      const admin = user?.email === "hello@zentrobooks.com";
      const target = admin ? "/admin/dashboard" : "/dashboard";
      console.log(`[GUARD] Access denied for feature=${feature}, redirecting to ${target}`);
      navigate(target);
    }
  }, [hasAccess, redirect, navigate, user, feature]);

  if (!hasAccess) {
    if (redirect) return null;
    if (fallback) return <>{fallback}</>;
    return <AccessDenied />;
  }

  return <>{children}</>;
}

/**
 * Route-level permission guard. Checks the current path against the
 * permission matrix. Use this to wrap ProtectedRoute for admin pages.
 */
export function RoutePermissionGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [location, navigate] = useLocation();

  const hasAccess = canAccessRoute(location, user);

  useEffect(() => {
    if (!hasAccess) {
      navigate("/dashboard");
    }
  }, [hasAccess, navigate, location]);

  if (!hasAccess) return null;

  return <>{children}</>;
}

function AccessDenied() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 text-center px-4">
      <AlertTriangle className="h-12 w-12 text-yellow-500" />
      <h2 className="text-xl font-semibold text-neutral-900">Access Restricted</h2>
      <p className="text-sm text-neutral-500 max-w-md">
        You don't have permission to access this feature. Contact your administrator
        if you believe this is an error.
      </p>
    </div>
  );
}
